import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Star, MessageCircle, Phone, CheckCircle2, Tag } from 'lucide-react';
import { HIMACHAL_TOURS, ITINERARY_SAMPLE, COMPANY } from '../mock';
import { PageHero } from './DestinationsPages';
import { Button } from '../components/ui/button';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import { getVideoReviews } from '../constants/videoReviews';

export function HimachalToursPage() {
  return (
    <div>
      <Helmet>
        <title>Top Himachal Packages - Best Selling Holiday Trips Starting From ₹8,499</title>
        <meta name="description" content="Best Selling Holiday Trips. Starting From ₹8,499. Explore our hand-crafted Himachal Pradesh tour packages from Shimla to Spiti Valley." />
        <meta property="og:title" content="Top Himachal Packages - Best Selling Holiday Trips Starting From ₹8,499" />
        <meta property="og:description" content="Best Selling Holiday Trips. Starting From ₹8,499. Explore our hand-crafted Himachal Pradesh tour packages from Shimla to Spiti Valley." />
      </Helmet>

      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src={HIMACHAL_TOURS[3].image} alt="Himachal Pradesh" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40">
              <span className="tracking-wide">Hand-Crafted Itineraries · Expert Local Guides</span>
            </div>
            <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl">
              <span className="text-brand-orange">Himachal</span>
              <br />
              <span>Tour Packages</span>
            </h1>
            <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg">
              From Shimla's colonial charm to Spiti's moonland - discover hand-crafted Himalayan journeys.
            </p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Popular</span>
              <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                Shimla · Manali · Spiti · Kasol · Dharamshala · Dalhousie
              </span>
            </div>
            <div className="flex flex-wrap gap-8 mt-10">
              {[
                { num: '50+', label: 'Tour Packages' },
                { num: '4.9', label: '★ Rating' },
                { num: '5000+', label: 'Happy Travelers' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-brand-orange font-poppins font-extrabold text-[28px] lg:text-[32px] leading-none">{stat.num}</div>
                  <div className="text-white/80 text-[12px] font-medium mt-1 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards destinationKey="himachal" />
      <CustomerVideoReviews reviews={getVideoReviews('himachal')} />

      <section id="top-himachal-packages" className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">{HIMACHAL_TOURS.length} Tours</div>
            <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Find your perfect <span className="text-brand-orange">Himachal trip</span></h2>
          </div>
          <Link to="/destinations/himachal-pradesh" className="text-brand-blue font-semibold border-b-2 border-brand-orange pb-1 hover:text-brand-orange transition-colors flex items-center gap-2">
            About Himachal <ArrowRight size={16}/>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {HIMACHAL_TOURS.map((t, i) => {
            const discount = Math.round(((t.oldPrice - t.price) / t.oldPrice) * 100);
            return (
              <Link key={t.slug} to={`/himachal-tours/${t.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-brand-greyDark shadow-card card-hover animate-fade-up" style={{animationDelay: `${i*60}ms`}}>
                <div className="relative aspect-[4/3] img-zoom-wrap overflow-hidden">
                  <img src={t.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover img-zoom" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-[11px] font-bold text-brand-blue">
                    <Clock size={11} className="text-brand-orange"/>{t.nights}N / {t.days}D
                  </div>
                  {discount > 0 && (
                    <div className="absolute top-3 right-3 bg-brand-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md flex items-center gap-1">
                      <Tag size={11}/>{discount}% OFF
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-poppins font-bold text-brand-blue text-[17px] leading-snug group-hover:text-brand-orange transition-colors line-clamp-2">{t.name}</h3>
                  <p className="text-brand-blue/65 text-[13px] mt-1.5 line-clamp-2">{t.tagline}</p>
                  <div className="flex items-center gap-3 mt-3 text-[12px] text-brand-blue/60">
                    <span className="flex items-center gap-1"><Star size={12} className="fill-brand-orange text-brand-orange"/>{t.rating} ({t.reviews})</span>
                    <span className="flex items-center gap-1"><MapPin size={12}/>{t.places.length} places</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-brand-grey flex items-end justify-between">
                    <div>
                      <div className="text-[10.5px] text-brand-blue/50 line-through">₹{t.oldPrice.toLocaleString('en-IN')}</div>
                      <div className="font-poppins font-bold text-brand-blue text-[19px] leading-none">₹{t.price.toLocaleString('en-IN')}<span className="text-[11px] text-brand-blue/55 font-normal">/person</span></div>
                    </div>
                    <div className="text-brand-orange font-semibold text-[12.5px] flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight size={13}/></div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <CTABanner/>
      <EnquiryForm/>
    </div>
  );
}

export function HimachalTourDetailPage() {
  const { slug } = useParams();
  const tour = HIMACHAL_TOURS.find(t => t.slug === slug);
  if (!tour) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center text-brand-blue">
      Tour not found. <Link to="/himachal-tours" className="text-brand-orange underline">Back to all Himachal tours</Link>
    </div>
  );
  const similar = HIMACHAL_TOURS.filter(t => t.slug !== slug).slice(0, 4);
  const discount = Math.round(((tour.oldPrice - tour.price) / tour.oldPrice) * 100);
  const itinerary = ITINERARY_SAMPLE.slice(0, tour.days);

  const META_BY_SLUG = {
    'shimla-tour': { title: 'Shimla Tour - Mall Road Kufri Chail - Packages From ₹8,499', desc: 'Mall Road Kufri Chail. Packages From ₹8,499. Book the Shimla Tour for a perfectly curated Himachal itinerary.' },
    'manali-tour': { title: 'Manali Tour - Snow Adventure Holidays - Packages From ₹10,999', desc: 'Snow Adventure Holidays. Packages From ₹10,999. Book the Manali Tour for a perfectly curated Himachal itinerary.' },
    'kinnaur-spiti-tour': { title: 'Kinnaur Spiti Tour - Chitkul Kalpa Sangla - Customized Road Trips', desc: 'Chitkul Kalpa Sangla. Customized Road Trips. Book the Kinnaur Spiti Tour for a perfectly curated Himachal itinerary.' },
  };
  const meta = META_BY_SLUG[slug];

  return (
    <div>
      <Helmet>
        <title>{meta ? meta.title : `${tour.name} | Mountains Chain Travel`}</title>
        <meta name="description" content={meta ? meta.desc : `Book the ${tour.name} for ₹${tour.price}. ${tour.nights} Nights / ${tour.days} Days perfectly curated Himachal itinerary by Mountains Chain Travel.`} />
        <meta property="og:title" content={meta ? meta.title : `${tour.name} | Mountains Chain Travel`} />
        <meta property="og:description" content={meta ? meta.desc : `Book the ${tour.name} for ₹${tour.price}. ${tour.nights} Nights / ${tour.days} Days perfectly curated Himachal itinerary by Mountains Chain Travel.`} />
      </Helmet>
      <PageHero title={tour.name} sub={`${tour.nights} Nights / ${tour.days} Days · Himachal Pradesh`} image={tour.image} />
      <ReviewCards destinationKey={slug} />
      <CustomerVideoReviews reviews={getVideoReviews(slug)} />
      <section id={slug} className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="flex items-center gap-1 text-brand-blue text-[14px] font-semibold"><Star size={15} className="fill-brand-orange text-brand-orange"/>{tour.rating} ({tour.reviews} reviews)</span>
              <span className="flex items-center gap-1 text-brand-blue/70 text-[14px]"><Clock size={14}/>{tour.nights}N / {tour.days}D</span>
              {discount > 0 && <span className="px-2.5 py-1 bg-brand-orange/10 text-brand-orange text-[11px] font-bold uppercase tracking-wider rounded-md">{discount}% OFF</span>}
            </div>

            <h2 className="font-poppins text-[26px] text-brand-blue mb-3">About this tour</h2>
            <p className="text-brand-blue/75 text-[15px] leading-relaxed">{tour.tagline}. Our {tour.name} is a hand-crafted itinerary built around comfortable pacing, verified hand-picked stays, and local guides who actually live in these valleys. Every detail - from cab to check-ins to meal recommendations - is taken care of so you can simply travel.</p>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-3">Places you will cover</h3>
            <div className="flex flex-wrap gap-2">
              {tour.places.map(p => (
                <span key={p} className="px-3 py-1.5 bg-brand-grey rounded-full text-[13px] text-brand-blue font-medium flex items-center gap-1.5">
                  <MapPin size={12} className="text-brand-orange"/>{p}
                </span>
              ))}
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-4">Tour highlights</h3>
            <ul className="space-y-2.5">
              {tour.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-blue/80 text-[14.5px]">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0"/>{h}
                </li>
              ))}
            </ul>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-10 mb-4">Day-by-day itinerary</h3>
            <div className="space-y-4">
              {itinerary.map(d => (
                <div key={d.day} className="flex gap-4 bg-brand-grey rounded-xl p-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-blue text-brand-orange font-poppins font-bold flex items-center justify-center shrink-0">D{d.day}</div>
                  <div>
                    <h4 className="font-poppins font-semibold text-brand-blue text-[15.5px]">{d.title}</h4>
                    <p className="text-brand-blue/70 text-[13.5px] mt-1 leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-10">
              <div>
                <h4 className="font-poppins font-semibold text-brand-blue mb-3">What's included</h4>
                <ul className="space-y-2">
                  {['Hand-picked hotel stay', 'AC vehicle for sightseeing & transfers', 'Daily breakfast (some packages include dinner)', 'Toll, parking & driver allowance', 'GST & service charges', '24x7 travel assistance'].map((x,i) => (
                    <li key={i} className="flex items-start gap-2 text-brand-blue/80 text-[14px]"><CheckCircle2 size={17} className="text-emerald-600 mt-0.5 shrink-0"/>{x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-poppins font-semibold text-brand-blue mb-3">Not included</h4>
                <ul className="space-y-2">
                  {['Personal expenses & beverages', 'Adventure activities (paragliding, rafting)', 'Entry fees to monuments', 'Travel insurance', 'Anything not listed above'].map((x,i) => (
                    <li key={i} className="flex items-start gap-2 text-brand-blue/65 text-[14px]"><span className="w-4 h-4 rounded-full border border-brand-blue/30 mt-1 shrink-0"/>{x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <aside className="bg-white border border-brand-greyDark rounded-2xl p-6 h-fit lg:sticky lg:top-24 shadow-card">
            {discount > 0 && <div className="inline-flex items-center gap-1 bg-brand-orange/10 text-brand-orange text-[11px] font-bold px-2 py-1 rounded-md mb-3"><Tag size={11}/>{discount}% OFF</div>}
            <div className="text-[12px] text-brand-blue/55 line-through">₹{tour.oldPrice.toLocaleString('en-IN')}</div>
            <div className="font-poppins font-bold text-brand-blue text-[34px] leading-none">₹{tour.price.toLocaleString('en-IN')}<span className="text-[13px] text-brand-blue/55 font-normal">/person</span></div>
            <div className="text-[12px] text-brand-blue/55 mt-1">{tour.nights}N · All taxes & transfers included</div>
            <div className="flex flex-col gap-2.5 mt-5">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m interested in the "' + tour.name + '" tour (' + tour.nights + 'N/' + tour.days + 'D) for ₹' + tour.price.toLocaleString('en-IN') + '/person.\n\nCould you share:\n- Complete day-wise itinerary\n- Hotel categories & meal plan\n- Best available discounts\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>WhatsApp for Best Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Book</Button>
              </a>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-grey text-[12.5px] text-brand-blue/70 space-y-2">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Customizable for any group size</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Pay just 20% to book</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>0% EMI available</div>
            </div>
          </aside>
        </div>

        <div className="mt-16">
          <h2 className="font-poppins text-[26px] text-brand-blue mb-6">Other Himachal tours you may like</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {similar.map(s => (
              <Link key={s.slug} to={`/himachal-tours/${s.slug}`} className="group rounded-xl overflow-hidden img-zoom-wrap relative h-48 block">
                <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover img-zoom"/>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent"/>
                <div className="relative h-full flex flex-col justify-end p-3 text-white">
                  <div className="text-[10.5px] text-white/75 uppercase tracking-wider">{s.nights}N / {s.days}D</div>
                  <h4 className="font-poppins font-bold text-[14px] leading-tight">{s.name}</h4>
                  <div className="text-brand-orange text-[12px] font-semibold mt-1">From ₹{s.price.toLocaleString('en-IN')}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <EnquiryForm/>
    </div>
  );
}
